/* =========================================================================
   HBH — Signature-Verified Delivery (cart page)
   Pair file: blocks/hbh-signature-choice.liquid

   Writes the shopper's answer to two cart attributes, which Shopify copies
   onto the order as note attributes ("Additional details" in the admin,
   `note_attributes` in the API, `attributes[...]` in notification emails):

     1. "Signature Delivery"            Required | Not required
     2. "Signature Delivery Agreement"  the sentence they chose

   Number 2 is the proof copy. It is read out of the block's own markup at the
   moment of the click, never held as a string in here, so the wording stored on
   the order is by construction the wording that was on the screen. The order
   confirmation email prints it verbatim, and that email is what gets shown to a
   carrier in a delivery dispute.

   Nothing is written to the cart note or to line item properties. The note is
   the shopper's own field and prints on packing slips; line properties repeat
   on every line in the admin, packing slips and OMS imports, and keeping them
   in sync needed one request per line. (Older carts that still carry the
   "[Signature Delivery: …]" note tag get it stripped on the next write.)

   Two rules everything else follows from:

   • A MISSING VALUE MEANS SIGNATURE REQUIRED. Never the other way round. A
     "Buy it now" order skips the cart and carries nothing at all, so blank has
     to be the protective answer.

   • THE DEFAULT IS WRITTEN ON CART LOAD, NOT ON CLICK. A shopper who never
     touches the box still leaves a real, recorded answer on the order, and an
     accelerated checkout (Shop Pay, PayPal) launched from the cart carries it.

   The cart section re-renders itself on a quantity change, which throws away
   and rebuilds this block's DOM. That is why every listener here is attached
   to `document` rather than to the inputs, and why the checked state is
   rendered by Liquid from the cart attribute rather than held in JavaScript.
   ========================================================================= */

(function () {
  'use strict';

  // Loaded once from layout/theme.liquid. Guard anyway, in case a theme
  // update or an app re-injects it.
  if (window.__hbhSignature) return;
  window.__hbhSignature = true;

  var ATTR = 'Signature Delivery'; // cart attribute name
  var ATTR_TEXT = 'Signature Delivery Agreement'; // the sentence they agreed to

  // Legacy note tag written by an earlier version. Only ever removed now.
  var NOTE_TAG = /\[Signature Delivery:[^\]]*\]/g;

  var busy = false;

  /* ---------------------------------------------------------------- cart API */

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('/cart.js -> ' + r.status);
      return r.json();
    });
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      if (!r.ok) throw new Error(url + ' -> ' + r.status);
      return r.json();
    });
  }

  // Anything that is not the exact opt-out string is treated as Required.
  // That is the fail-safe rule, expressed as one line of code.
  function normalize(v) {
    return v === 'Not required' ? 'Not required' : 'Required';
  }

  // Strip only our own legacy bracketed tag, so a note the shopper or another
  // app wrote survives untouched. Returns null when there is nothing to strip,
  // so the note is left out of the request entirely.
  function noteCleaned(note) {
    var s = String(note || '');
    if (!NOTE_TAG.test(s)) return null;
    NOTE_TAG.lastIndex = 0;
    return s.replace(NOTE_TAG, '').replace(/\n{2,}/g, '\n').trim();
  }

  /* The exact sentence sitting next to the chosen option, lifted from the
     markup. Lead and fine print joined, whitespace collapsed — Liquid indents
     its output, and a stored agreement full of line breaks reads badly in the
     admin and worse in an email. Returns '' if the block is not on the page,
     and callers then leave the stored copy alone rather than blanking it. */
  function statementFor(choice) {
    var all = radios();
    var input = null;
    for (var i = 0; i < all.length; i++) {
      if (all[i].value === choice) { input = all[i]; break; }
    }
    var label = input && input.closest('.hbh-sig__opt');
    if (!label) return '';

    var parts = [];
    var lead = label.querySelector('[data-hbh-lead]');
    var fine = label.querySelector('[data-hbh-fine]');
    if (lead) parts.push(lead.textContent);
    if (fine) parts.push(fine.textContent);
    return parts.join(' ').replace(/\s+/g, ' ').trim();
  }

  function writeAttribute(choice, cart) {
    var body = { attributes: {} };
    body.attributes[ATTR] = choice;

    var text = statementFor(choice);
    if (text) body.attributes[ATTR_TEXT] = text;

    var cleaned = noteCleaned(cart.note);
    if (cleaned !== null) body.note = cleaned;

    return post('/cart/update.js', body);
  }

  /* ------------------------------------------------------------------- paint */

  function roots() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-hbh-sig]'));
  }

  // Prefix, not an exact name: the cart page and the cart drawer each render
  // their own radio group, so that a page showing both can show both as
  // checked. One shared name would make them a single group and the browser
  // would allow only one checked radio between them.
  function radios() {
    return Array.prototype.slice.call(document.querySelectorAll('input[name^="hbh-signature"]'));
  }

  function setBusy(on) {
    roots().forEach(function (el) {
      if (on) el.setAttribute('data-busy', '');
      else el.removeAttribute('data-busy');
    });
    radios().forEach(function (i) {
      i.disabled = !!on;
    });
  }

  function paintChoice(choice) {
    roots().forEach(function (el) {
      el.setAttribute('data-choice', choice);
    });
    radios().forEach(function (i) {
      i.checked = i.value === choice;
    });
  }

  function status(msg, isError) {
    document.querySelectorAll('[data-hbh-sig-status]').forEach(function (el) {
      el.textContent = msg || '';
      if (isError) el.setAttribute('data-error', '');
      else el.removeAttribute('data-error');
    });
  }

  /* Re-render the cart section through Shopify's Section Rendering API — the
     same mechanism the theme itself uses for a quantity change. The server
     re-renders our radios from the saved attribute, so the selection comes
     back correct on its own. Failure here is cosmetic: the answer is already
     saved by the time this runs. */
  function refreshCartSection() {
    var root = document.querySelector('[data-hbh-sig]');
    if (!root) return Promise.resolve();

    // Never inside the drawer. The drawer is not a section, and re-rendering
    // whatever section surrounds it would tear it down while the shopper is
    // still looking at it. The drawer refreshes itself on the next cart change.
    if (root.closest('cart-drawer-component')) return Promise.resolve();

    var wrap = root.closest('[id^="shopify-section-"]');
    if (!wrap) return Promise.resolve();

    var id = wrap.id.replace('shopify-section-', '');
    return fetch(window.location.pathname + '?section_id=' + encodeURIComponent(id), {
      headers: { Accept: 'text/html' },
    })
      .then(function (r) {
        return r.ok ? r.text() : null;
      })
      .then(function (html) {
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.getElementById(wrap.id) || doc.body.firstElementChild;
        if (fresh) wrap.innerHTML = fresh.innerHTML;
      })
      .catch(function () {
        /* cosmetic only */
      });
  }

  /* ------------------------------------------------------------------- apply */

  function apply(choice, opts) {
    choice = normalize(choice);
    opts = opts || {};

    // The radios are disabled while a save is in flight, so a second click
    // cannot reach here. This is belt and braces.
    if (busy) return Promise.resolve();
    busy = true;

    // The radio the shopper just used, so focus can be put back after the
    // section is re-rendered out from under it.
    var hadFocus =
      document.activeElement &&
      document.activeElement.name &&
      document.activeElement.name.indexOf('hbh-signature') === 0;

    if (!opts.silent) {
      setBusy(true);
      status('Saving…');
    }

    // reconcile() has already read the cart; re-reading it here would cost a
    // second round trip and open a window for the two reads to disagree.
    var start = opts.cart ? Promise.resolve(opts.cart) : getCart();

    return start
      .then(function (cart) {
        if (!cart.item_count) return null;

        // Skip the write when both attributes already say the right thing
        // and there is no legacy note tag to clean up.
        var attrs = cart.attributes || {};
        var text = statementFor(choice);
        var attrsOk =
          attrs[ATTR] === choice &&
          (!text || attrs[ATTR_TEXT] === text) &&
          noteCleaned(cart.note) === null;

        return attrsOk ? Promise.resolve(cart) : writeAttribute(choice, cart);
      })
      .then(function (done) {
        if (!done) return;
        if (opts.silent) {
          paintChoice(choice);
          return;
        }
        return refreshCartSection().then(function () {
          paintChoice(choice);
          status(
            choice === 'Required'
              ? 'Saved — a signature will be required.'
              : 'Saved — no signature required.'
          );
          if (hadFocus) {
            var back = radios().filter(function (i) {
              return i.value === choice;
            })[0];
            if (back) back.focus();
          }
        });
      })
      .catch(function (e) {
        if (!opts.silent) status('Couldn’t save that. Please try again.', true);
        if (window.console && console.warn) console.warn('[HBH Signature] ' + e);
      })
      .then(function () {
        busy = false;
        setBusy(false);
      });
  }

  /* --------------------------------------------------------------- reconcile */

  /* Runs on load and again whenever the cart section changes. Writes the
     default the first time the cart has an item and no answer yet. Silent,
     because the shopper never asked for this and must not be shown "Saving…"
     out of nowhere. It also does not re-render the section: nothing on screen
     is wrong yet, and a re-render during page load would flash. */
  function reconcile() {
    if (!document.querySelector('[data-hbh-sig]')) return;
    watch(); // cheap re-arm in case the section wrapper itself was swapped out
    if (busy) return;

    getCart()
      .then(function (cart) {
        if (!cart.item_count) return;
        var saved = cart.attributes && cart.attributes[ATTR];
        var choice = normalize(saved);

        // If the wording in the block has been edited since this cart was
        // answered, the stored proof copy is out of date — rewrite it, because
        // the sentence on the order has to be the sentence on the screen.
        var wantText = statementFor(choice);
        var haveText = (cart.attributes && cart.attributes[ATTR_TEXT]) || '';
        var textOk = !wantText || haveText === wantText;

        if (saved === choice && textOk && noteCleaned(cart.note) === null) {
          paintChoice(choice);
          return;
        }
        return apply(choice, { silent: true, cart: cart });
      })
      .catch(function (e) {
        if (window.console && console.warn) console.warn('[HBH Signature] ' + e);
      });
  }

  /* ---------------------------------------------------------------- listeners */

  // Delegated, so it survives the cart section being torn down and rebuilt.
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (t && t.name && t.name.indexOf('hbh-signature') === 0) apply(t.value);
  });

  var timer = null;
  function reconcileSoon() {
    clearTimeout(timer);
    timer = setTimeout(reconcile, 700);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }

  // The block is not always on the page when this file runs. On a product
  // page the cart drawer (and our block inside it) is fetched and injected
  // only after the first "Add to cart", and the cart section re-renders on a
  // quantity change, a line removal, or an add from a recommendation block.
  // A <script> inside injected HTML never executes, which is why this file is
  // loaded once from layout/theme.liquid and watches the whole document for
  // the block to appear or change, rather than being loaded by the block.
  // The observer only schedules a debounced reconcile(), and reconcile()
  // returns at once when no block is present, so the cost on pages without a
  // cart is one cheap querySelector per burst of DOM changes.
  function watch() {
    if (document.body.__hbhWatched) return;
    document.body.__hbhWatched = true;
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var m = muts[i];
        var t = m.target;
        // Ignore churn that cannot involve our block: text-only edits, and
        // changes inside nodes that neither contain nor sit inside it.
        if (m.type !== 'childList') continue;
        if (t && t.closest && (t.closest('[data-hbh-sig]') || t.querySelector('[data-hbh-sig]'))) {
          reconcileSoon();
          return;
        }
        for (var j = 0; j < m.addedNodes.length; j++) {
          var n = m.addedNodes[j];
          if (n.nodeType === 1 && (n.matches('[data-hbh-sig]') || n.querySelector('[data-hbh-sig]'))) {
            reconcileSoon();
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.body) watch();
  else document.addEventListener('DOMContentLoaded', watch);

  // Theme editor: re-run when a section or block is re-rendered or selected.
  document.addEventListener('shopify:section:load', function () {
    watch();
    reconcileSoon();
  });
  document.addEventListener('shopify:block:select', reconcileSoon);
})();
